/**
 * Name Input Component
 *
 * Collects first and last name
 */

"use client";

import { round } from "lodash";
import { Check } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorState,
} from "../insurance-connector-provider";
import type { InsuranceConnectorContext } from "../machine/types";

function calculateProgress(
  context: InsuranceConnectorContext,
  stateValue: string | Record<string, unknown>,
) {
  // ===== PERSONAL INFO PROGRESS (50% of total) =====
  // Step 1     | 1/5 | Started
  // Step 1.1   | 2/5 | First name + last name
  // Step 1.2   | 3/5 | Date of birth
  // Step 1.3   | 4/5 | Phone number + loading state when we check for returning consumer
  // Step 1.3.1 | 5/5 | We matched to an existing consumer, Need to enter OTP 6 digit code

  let personalInfoNumerator = 1; // Start at 1/5
  const personalInfoDenominator = 5;

  if (context.firstName && context.lastName) {
    personalInfoNumerator = 2; // 2/5
  }
  if (context.birthday) {
    personalInfoNumerator = 3; // 3/5
  }
  if (context.phoneNumber) {
    personalInfoNumerator = 4; // 4/5
  }

  // Personal info is complete when:
  // 1. Return user completed OTP verification (verified === true)
  // 2. We've reached any insurance state (insurancePayer, memberIdInput, etc.)
  // 3. We're in the OTP verification flow (all personal info has been entered)
  const fullState =
    typeof stateValue === "object" && "full" in stateValue
      ? String(stateValue.full)
      : String(stateValue);

  const insuranceStates = [
    "insurancePayer",
    "memberIdInput",
    "insuredStateInput",
    "secondaryQuestion",
    "secondaryPayerInput",
    "secondaryMemberIdInput",
    "secondaryInsuredStateInput",
  ];
  const isInInsuranceSection = insuranceStates.includes(fullState);

  const otpStates = ["sendingSMS", "otpInput", "verifyingSMS"];
  const isInOTPSection = otpStates.includes(fullState);

  if (
    context.verified ||
    isInOTPSection ||
    (context.phoneNumber && isInInsuranceSection)
  ) {
    personalInfoNumerator = 5; // 5/5
  }

  const personalInfoProgress = round(
    personalInfoNumerator / personalInfoDenominator,
    2,
  );

  // ===== INSURANCE INFO PROGRESS (50% of total) =====
  // Step 2     | 1/5 | Started insurance section
  // Step 2.1   | 2/5 | Primary Insurance payer
  // Step 2.2   | 3/5 | Primary Insurance memberId
  // Step 2.3   | 4/5 | Primary Insurance state
  // Step 2.4   | 5/5 | Secondary insurance question - 100% if no secondary
  // Step 2.5   | 6/8 | Secondary Insurance payer (if has secondary)
  // Step 2.6   | 7/8 | Secondary Insurance memberId
  // Step 2.7   | 8/8 | Secondary Insurance state - 100%, then loading

  // User said YES to secondary if we're in any secondary input state
  const saidYesToSecondary =
    fullState === "secondaryPayerInput" ||
    fullState === "secondaryMemberIdInput" ||
    fullState === "secondaryInsuredStateInput";

  let insuranceNumerator = 1; // Start at 1/5 or 1/8
  // Use denominator 8 if user said YES to secondary OR already has secondary data
  const insuranceDenominator =
    saidYesToSecondary || context.secondaryInsurance ? 8 : 5;

  // Don't count pre-filled insurance data from return user lookup
  // until identity is confirmed via OTP
  const shouldCountInsuranceContext = context.verified || isInInsuranceSection;

  // Primary insurance steps - use state + context for accuracy
  if (
    (shouldCountInsuranceContext && context.primaryInsurance?.payer) ||
    fullState === "memberIdInput"
  ) {
    insuranceNumerator = 2; // 2/5 or 2/8
  }
  if (
    (shouldCountInsuranceContext && context.primaryInsurance?.memberId) ||
    fullState === "insuredStateInput"
  ) {
    insuranceNumerator = 3; // 3/5 or 3/8
  }
  if (
    (shouldCountInsuranceContext && context.primaryInsurance?.insuredState) ||
    fullState === "secondaryQuestion"
  ) {
    insuranceNumerator = 4; // 4/5 or 4/8
  }

  // Secondary insurance question answered
  if (
    (shouldCountInsuranceContext &&
      context.primaryInsurance?.payer &&
      context.primaryInsurance?.memberId &&
      context.primaryInsurance?.insuredState) ||
    fullState === "secondaryQuestion" ||
    saidYesToSecondary
  ) {
    insuranceNumerator = 5; // 5/5 (100% if no secondary) or 5/8
  }

  // Secondary insurance steps - use state + context
  if (
    (shouldCountInsuranceContext && context.secondaryInsurance?.payer) ||
    fullState === "secondaryMemberIdInput"
  ) {
    insuranceNumerator = 6; // 6/8
  }
  if (
    (shouldCountInsuranceContext && context.secondaryInsurance?.memberId) ||
    fullState === "secondaryInsuredStateInput"
  ) {
    insuranceNumerator = 7; // 7/8
  }
  if (shouldCountInsuranceContext && context.secondaryInsurance?.insuredState) {
    insuranceNumerator = 8; // 8/8 (100%)
  }

  const insuranceProgress = round(insuranceNumerator / insuranceDenominator, 2);

  // Show insurance progress once we've completed personal info OR if we're already in insurance section
  const hasStartedInsurance =
    shouldCountInsuranceContext &&
    !!(
      context.primaryInsurance?.payer ||
      context.primaryInsurance?.memberId ||
      context.primaryInsurance?.insuredState
    );

  return {
    personalInfo: personalInfoProgress,
    insurance:
      personalInfoProgress === 1 || hasStartedInsurance ? insuranceProgress : 0,
  };
}

export function LoadingBar() {
  const t = useTranslations("InsuranceConnector");
  const context = useInsuranceConnectorContext();
  const state = useInsuranceConnectorState();
  const progress = calculateProgress(context, state.value);

  // Checkmark turns blue when insurance is complete
  const isInsuranceComplete = progress.insurance === 1;

  return (
    <div className="flex flex-col gap-2">
      {/* Progress bars and check mark - centered together */}
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="w-full gap-2 flex flex-row items-center">
          <div className="relative bg-white h-2 rounded-full w-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full transition-all duration-300 rounded-full"
              style={{
                width: `${progress.personalInfo * 100}%`,
                backgroundColor: "#00C2FF",
              }}
            />
          </div>
          <div className="relative bg-white h-2 rounded-full w-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full transition-all duration-300 rounded-full"
              style={{
                width: `${progress.insurance * 100}%`,
                backgroundColor: "#00C2FF",
              }}
            />
          </div>
        </div>
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-300"
          style={{
            backgroundColor: isInsuranceComplete ? "#00C2FF" : "#FFFFFF",
          }}
        >
          <Check
            className={`h-3 w-3 stroke-2 transition-all duration-300 ${
              isInsuranceComplete ? "text-white" : "text-black"
            }`}
          />
        </div>
      </div>

      {/* Labels below bars */}
      <div className="flex flex-row gap-2 justify-between">
        <div className="flex flex-row gap-2 w-full">
          <p
            className={`w-full transition-colors duration-300 ${
              progress.personalInfo > 0 && progress.personalInfo < 1
                ? "font-semibold text-black"
                : "text-[#6D757F]"
            }`}
          >
            {t("PersonalInfo")}
          </p>
          <p
            className={`w-full transition-colors duration-300 ${
              progress.personalInfo === 1 && progress.insurance < 1
                ? "font-semibold text-black"
                : "text-[#6D757F]"
            }`}
          >
            {t("Insurance")}
          </p>
        </div>
        {/* Spacer to match check mark width */}
        <div className="w-6 shrink-0" />
      </div>
    </div>
  );
}
